import { Box, FloraInput, FloraSelect, FloraSelectItem, Grid } from '@grupoboticario/flora-react'

import { CAMPAIGN_BRAND_CODES, CampaignBrandCode } from '@/constants/campaignBrands'

import type { CampaignBrandFlags, ContentByBrandForm } from '../AccelerationForm.types'

interface CampaignBrandsSectionProps {
  brand: CampaignBrandFlags
  contentByBrand: ContentByBrandForm
  buttonUrl?: string
  allowLegacyButtonUrl?: boolean
  readOnly?: boolean
  onBrandChange: (brandCode: CampaignBrandCode, checked: boolean) => void
  onBrandButtonUrlChange: (brandCode: CampaignBrandCode, buttonUrl: string) => void
  onButtonUrlChange?: (buttonUrl: string) => void
}

const syncBrandSelection = (
  brand: CampaignBrandFlags,
  selectedValues: string[],
  onBrandChange: (brandCode: CampaignBrandCode, checked: boolean) => void
) => {
  const selectedCodes = new Set(selectedValues)

  CAMPAIGN_BRAND_CODES.forEach((brandCode) => {
    const shouldBeChecked = selectedCodes.has(brandCode)
    if (brand[brandCode] !== shouldBeChecked) {
      onBrandChange(brandCode, shouldBeChecked)
    }
  })
}

export { syncBrandSelection }

const CampaignBrandsSection = ({
  brand,
  contentByBrand,
  buttonUrl = '',
  allowLegacyButtonUrl = false,
  readOnly = false,
  onBrandChange,
  onBrandButtonUrlChange,
  onButtonUrlChange
}: CampaignBrandsSectionProps) => {
  const selectedBrands = CAMPAIGN_BRAND_CODES.filter((code) => brand[code])
  const showLegacyButtonUrl = allowLegacyButtonUrl && selectedBrands.length === 0

  return (
    <Box data-testid="campaign-brands-section">
      <FloraSelect
        data-testid="campaignBrandsSelect"
        disabled={readOnly}
        hasMultipleControls
        id="campaignBrands"
        label="Marcas participantes"
        multiple
        onClearAll={() => syncBrandSelection(brand, [], onBrandChange)}
        onSelected={(selected) =>
          syncBrandSelection(
            brand,
            selected.map(({ value }) => value),
            onBrandChange
          )
        }
        onSelectAll={(values) =>
          syncBrandSelection(
            brand,
            values.map(({ value }) => value),
            onBrandChange
          )
        }
        placeholder="Selecionar"
        required
        size="medium"
        value={selectedBrands}
      >
        {CAMPAIGN_BRAND_CODES.map((brandCode) => (
          <FloraSelectItem key={brandCode} label={brandCode} value={brandCode} />
        ))}
      </FloraSelect>

      {showLegacyButtonUrl && (
        <Box css={{ marginTop: '$4' }}>
          <FloraInput
            data-testid="buttonUrlInput"
            id="buttonUrl"
            label="URL de destino"
            onChange={(event) => onButtonUrlChange?.(event.target.value)}
            readOnly={readOnly}
            disabled={readOnly}
            required
            size="medium"
            value={buttonUrl}
          />
        </Box>
      )}

      {selectedBrands.length > 0 && (
        <Grid css={{ marginTop: '$4' }} gap="$4" templateColumns="repeat(2, 1fr)">
          {selectedBrands.map((brandCode) => (
            <FloraInput
              key={brandCode}
              data-testid={`brandButtonUrl-${brandCode}`}
              id={`brandButtonUrl-${brandCode}`}
              label={`URL de destino (${brandCode})`}
              onChange={(event) => onBrandButtonUrlChange(brandCode, event.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
              required
              size="medium"
              value={contentByBrand[brandCode]?.buttonUrl ?? ''}
            />
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default CampaignBrandsSection
